import { QuestionCircleOutlined } from '@ant-design/icons'
import { type Home, type User, UserRole } from '@lmc/ts-common'
import {
  Button,
  Form,
  FormRadio,
  FormRadioGroup,
  Input,
  FormItem as LMCFormItem,
  Select
} from '@lmc/ui-kit'
import { Heading, Text } from '@lmc/ui-kit/src/general'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import { Form as AntdForm, Modal } from 'antd'
import { showToast } from 'common/utils'
import useUser from 'hooks/useUser/useUser'
import { NAMESPACE } from 'locale'
import { generalConstants } from 'locale/general'
import { reportingConstants } from 'locale/reporting'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { commonPatterns } from '../../../../../../src/utils/enhancedTestIds'

const FormItem = (props: React.ComponentProps<typeof LMCFormItem>) => (
  <LMCFormItem labelCol={{ style: { width: 60 } }} {...props} />
)

export type AddTeamMemberProps = {
  open: boolean
  onClose: () => void
  type: 'addTeamMember' | 'addGroupDashboardUser'
  testId?: string
}

const HELP_URL =
  'https://support.logmycare.co.uk/en/articles/3885227-how-do-i-add-a-new-group-manager-or-group-admin'

const AddTeamMemberModal = ({
  open,
  onClose,
  type,
  testId
}: AddTeamMemberProps) => {
  const [form] = AntdForm.useForm()

  const { t } = useTranslation(NAMESPACE.GENERAL)

  const { t: tReporting } = useTranslation(NAMESPACE.REPORTING)

  const isGroupDashboard = type === 'addGroupDashboardUser'

  const user = useSelector(
    (state: { global: { user: User } }) => state.global.user
  )

  const homes = useSelector(
    (state: { data: { homes: Home[] } }) => state.data.homes
  )

  const roleInfoText = isGroupDashboard
    ? t(generalConstants.ROLE_INFO_TEXT_GROUP_DASHBOARD_USER)
    : t(generalConstants.ROLE_INFO_TEXT_TEAM_MEMBER)

  const modalTitle = isGroupDashboard
    ? t(generalConstants.INVITE_A_NEW_GROUP_DASHBOARD_USER)
    : t(generalConstants.INVITE_A_NEW_TEAM_MEMBER)

  const roles = useMemo(() => {
    const teamMemberRoles = [
      {
        label: tReporting(reportingConstants[UserRole.carer]),
        value: UserRole.carer
      },
      {
        label: tReporting(reportingConstants[UserRole.seniorCarer]),
        value: UserRole.seniorCarer
      },
      {
        label: tReporting(reportingConstants[UserRole.careHomeAdmin]),
        value: UserRole.careHomeAdmin
      }
    ]
    const groupDashboardUserRoles = [
      {
        label: tReporting(reportingConstants[UserRole.groupManager]),
        value: UserRole.groupManager
      },
      {
        label: tReporting(reportingConstants[UserRole.groupAdmin]),
        value: UserRole.groupAdmin
      }
    ]

    return isGroupDashboard ? groupDashboardUserRoles : teamMemberRoles
  }, [type])

  const { createUser, isLoadingCreate } = useUser({
    onCreateSuccess: () => {
      onClose()
      showToast({
        title: t(
          generalConstants?.[
            isGroupDashboard
              ? 'ADDED_GROUP_DASHBOARD_USER'
              : 'ADDED_TEAM_MEMBER'
          ]
        ),
        type: 'success'
      })
    },
    onErrorCustom: (err) => {
      // eslint-disable-next-line prefer-destructuring
      const message = (err as unknown as { message: string }).message
      form.setFields([
        {
          name: 'email',
          errors: [message]
        }
      ])
    }
  })

  const isLoading = isLoadingCreate

  const initialValues = useMemo(
    () => ({
      allSites: false,
      role: isGroupDashboard ? UserRole.groupManager : UserRole.careHomeAdmin
    }),
    []
  )

  const onFinish = async (
    values: Record<string, unknown> & { homes: string[] }
  ) => {
    if (isGroupDashboard) {
      createUser({ ...values, homeGroup: user?.homeGroup })

      return
    }
    createUser({ ...values, home: values?.homes?.[0] })
  }

  const sites = useMemo(
    () =>
      homes.map((home) => ({
        value: home?.id,
        label: home?.name
      })),
    [homes]
  )

  const filterOption = (input: string, option?: { label: string }) =>
    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())

  return (
    <Modal
      destroyOnClose
      width={580}
      title={
        <ModalHeader data-testid={testId ? `${testId}-header` : undefined}>
          <Title level={1} data-testid={testId ? `${testId}-title` : undefined}>
            {modalTitle}
          </Title>
          <GrayText
            data-testid={testId ? `${testId}-required-fields` : undefined}
          >
            {t(generalConstants.REQUIRED_FIELDS_ARE_MARKED_WITH)}{' '}
            <RedText>*</RedText>
          </GrayText>
        </ModalHeader>
      }
      footer={
        <ModalFooter data-testid={testId ? `${testId}-footer` : undefined}>
          <Button
            onClick={onClose}
            data-testid={
              testId ? commonPatterns.button(testId, 'cancel') : undefined
            }
          >
            {t('CANCEL')}
          </Button>
          <Button
            onClick={form.submit}
            variant="primary"
            isLoading={isLoading}
            data-testid={
              testId ? commonPatterns.button(testId, 'confirm') : undefined
            }
          >
            {t(generalConstants.CONFIRM)}
          </Button>
        </ModalFooter>
      }
      open={open}
      onCancel={onClose}
      onOk={form.submit}
      data-testid={testId}
    >
      <ModalDivider data-testid={testId ? `${testId}-divider` : undefined} />
      <StyledForm
        form={form}
        initialValues={initialValues}
        onFinish={onFinish}
        data-testid={testId ? `${testId}-form` : undefined}
      >
        <NameContainer
          data-testid={testId ? `${testId}-name-container` : undefined}
        >
          <NameLabel data-testid={testId ? `${testId}-name-label` : undefined}>
            <RedText>*</RedText> {`${t('NAME')}:`}
          </NameLabel>
          <div data-testid={testId ? `${testId}-first-name-field` : undefined}>
            <FormItem
              name="name.first"
              rules={[
                {
                  required: true,
                  message: t(generalConstants.FIRST_NAME_IS_REQUIRED)
                },
                {
                  max: 35,
                  message: t(generalConstants.FIRST_NAME_MAX_LENGTH)
                }
              ]}
            >
              <Input
                maxLength={35}
                placeholder={t('FIRST')}
                data-testid={
                  testId
                    ? commonPatterns.input(testId, 'first-name')
                    : undefined
                }
              />
            </FormItem>
          </div>
          <div data-testid={testId ? `${testId}-last-name-field` : undefined}>
            <FormItem
              name="name.last"
              rules={[
                {
                  required: true,
                  message: t(generalConstants.LAST_NAME_IS_REQUIRED)
                },
                {
                  max: 35,
                  message: t(generalConstants.LAST_NAME_MAX_LENGTH)
                }
              ]}
            >
              <Input
                maxLength={35}
                placeholder={t('LAST')}
                data-testid={
                  testId ? commonPatterns.input(testId, 'last-name') : undefined
                }
              />
            </FormItem>
          </div>
        </NameContainer>
        <div data-testid={testId ? `${testId}-email-field` : undefined}>
          <FormItem
            name="email"
            label={t(generalConstants.EMAIL)}
            rules={[
              {
                required: true,
                message: t(generalConstants.EMAIL_IS_REQUIRED)
              }
            ]}
          >
            <Input
              type="email"
              data-testid={
                testId ? commonPatterns.input(testId, 'email') : undefined
              }
            />
          </FormItem>
        </div>

        <div data-testid={testId ? `${testId}-role-container` : undefined}>
          <div data-testid={testId ? `${testId}-role-field` : undefined}>
            <FormItem
              name="role"
              label={t(generalConstants.ROLE)}
              rules={[{ required: true }]}
              extra={
                <RoleInfo
                  data-testid={testId ? `${testId}-role-info` : undefined}
                >
                  <span>{roleInfoText}</span>
                  {isGroupDashboard && (
                    <Link
                      to={HELP_URL}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={testId ? `${testId}-help-link` : undefined}
                    >
                      <QuestionIcon />
                    </Link>
                  )}
                </RoleInfo>
              }
            >
              <StyledRadioGroup
                direction="row"
                data-testid={testId ? `${testId}-role-radio-group` : undefined}
              >
                {roles.map(({ label, value }) => (
                  <StyledFormRadio
                    key={value}
                    value={value}
                    text={label}
                    data-testid={testId ? `${testId}-role-${value}` : undefined}
                  />
                ))}
              </StyledRadioGroup>
            </FormItem>
          </div>
        </div>
        {!isGroupDashboard && (
          <SitesContainer
            data-testid={testId ? `${testId}-sites-container` : undefined}
          >
            <div data-testid={testId ? `${testId}-sites-field` : undefined}>
              <FormItem
                name="homes"
                label={t(generalConstants.SITES)}
                rules={[{ required: true }]}
              >
                <Select
                  filterOption={filterOption}
                  mode="multiple"
                  placeholder={t(generalConstants.SELECT_SITES)}
                  options={sites}
                  data-testid={testId ? `${testId}-sites-select` : undefined}
                  dropdownRender={(menuOptions) => (
                    <>
                      {menuOptions}
                      <>
                        <StyledDivider
                          data-testid={
                            testId ? `${testId}-sites-divider` : undefined
                          }
                        />
                        <ButtonsContainer
                          data-testid={
                            testId ? `${testId}-sites-buttons` : undefined
                          }
                        >
                          <StyledButton
                            onClick={() => form.setFieldValue('homes', [])}
                            data-testid={
                              testId
                                ? `${testId}-sites-clear-button`
                                : undefined
                            }
                          >
                            <Text level={1}>Clear</Text>
                          </StyledButton>
                          {sites && (
                            <StyledButton
                              onClick={() =>
                                form.setFieldValue(
                                  'homes',
                                  sites?.map((item) => item.value)
                                )
                              }
                              data-testid={
                                testId
                                  ? `${testId}-sites-select-all-button`
                                  : undefined
                              }
                            >
                              <Text level={1}>Select All</Text>
                            </StyledButton>
                          )}
                        </ButtonsContainer>
                      </>
                    </>
                  )}
                />
              </FormItem>
            </div>
          </SitesContainer>
        )}
      </StyledForm>
      <ModalDivider />
    </Modal>
  )
}

export default AddTeamMemberModal

const Title = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color3};
  text-transform: none;
  font-weight: 500;
  font-size: 24px;
  line-height: 32px;
`

const ModalDivider = styled(Divider)`
  margin: 24px 0px;
`

const StyledForm = styled(Form)`
  gap: 24px;
`

const ModalFooter = styled.div`
  display: flex;
  gap: 16px;
`

const ModalHeader = styled.div`
  display: flex;
  flex-direction: column;
  text-transform: none;
`

const GrayText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`

const RedText = styled(Text)`
  color: ${(props) => props.theme.color.status.danger};
`

const NameLabel = styled.div`
  display: flex;
  gap: 3px;
  align-items: center;
  width: 52px;
`

const NameContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`

const StyledFormRadio = styled(FormRadio)`
  text-transform: capitalize;
`

const RoleInfo = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;
`

const QuestionIcon = styled(QuestionCircleOutlined)`
  font-size: 16px;
  margin-top: 4px;
  margin-right: 4px;
  color: ${(props) => props.theme.color.press};
`

// const AllSitesCheckboxContainer = styled.div`
//   margin-left: 60px;
// `

const SitesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const StyledRadioGroup = styled(FormRadioGroup)`
  & > * {
    width: auto;
  }
`

const StyledDivider = styled(Divider)`
  margin: 4px 0;
`

const ButtonsContainer = styled.div`
  padding: 8px 16px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`

const StyledButton = styled.button`
  all: unset;
  cursor: pointer;
  color: ${(props) => props.theme.color.status.primary};
  :hover {
    opacity: 0.7;
  }
`
